import React, { useState, useEffect, useRef, createContext } from 'react';
import { motion, useAnimationControls, useInView, AnimatePresence, useMotionValue, useTransform, useSpring } from 'framer-motion';
import { useRoute, linkProps } from './lib/router';
import Dissertation from './pages/Dissertation';
import { Linkedin, Mail, GitBranch, GraduationCap, Briefcase, Smartphone, Gamepad2, ArrowRight, Shield, Wrench, Building2, LayoutGrid, Github } from 'lucide-react';

// --- Theme Context ---
const ThemeContext = createContext();
const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('dark');
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove(theme === 'dark' ? 'light' : 'dark');
    root.classList.add(theme);
  }, [theme]);
  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');
  return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>;
};
// --- Icons & Data ---
const GoogleScholarIcon = (props) => (
  <svg role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor" {...props}><title>Google Scholar</title><path d="M5.242 13.769L0 9.5 12 0l12 9.5-5.242 4.269C17.548 11.249 14.978 9.5 12 9.5c-2.977 0-5.548 1.748-6.758 4.269zM12 10a7 7 0 1 0 0 14 7 7 0 0 0 0-14z"/></svg>
);
const portfolioData = { name: "Nitant Upasani", title: "Researcher, Designer and Developer", summary: "Aspiring to make a positive contribution to society and the environment through scientific openness, inclusivity, and lifelong learning.", pictureUrl: "/picture.png", contact: { email: "n.a.upasani@tue.nl", linkedin: "https://www.linkedin.com/in/nitant-upasani-bb1a76148/", googleScholar: "https://scholar.google.com/citations?user=2uA0rkUAAAAJ&hl=en", github: "https://github.com/nitantupasani" }, about: { philosophy: "My philosophy involves scientific openness, inclusivity, and lifelong learning. As a mathematician, I enjoy modeling and solving both abstract and real-world problems.", strengths: "My perceived strengths are perseverance, an affinity for technology, and a strong will and curiosity to learn new things.", values: "I value kindness, humor, and ambition in the workplace and do my best to embody these qualities myself." }, skills: ["C/C++", "Python", "MATLAB", "MySQL", "HTML/CSS", "JavaScript", ".NET", "Angular", "React", "Flutter", "Dart", "LaTeX"], timeline: [{ type: 'work', title: 'Smart Building Engineer', institution: 'Spectral Energy', period: 'Sept 2025 - Present', description: "Monitoring and controlling building HVAC conditioning to for energy and comfort optimization. BRIGHTER - the smart building energy platform has a portfolio of over 100 buildings. " }, { type: 'work', title: 'Co-founder and Tech Lead', institution: 'GPLAN.in', period: 'Jan 2018 - Present', description: 'Developed graph-based floorplanning algorithms for a web application for architects.' },{ type: 'work', title: 'PhD Researcher', institution: 'Brains4Buildings', period: 'Sept 2021 - Present', description: "Conducting occupant-centered comfort research for optimized HVAC control. Sole PhD in WP3 'Smart User targeted interfaces and feedback'." }, { type: 'work', title: 'Full Stack Developer', institution: 'Q3 Technologies, India', period: 'Aug 2020 - July 2021', description: 'Worked on web development projects with technologies like Angular, React, and .NET.' }, { type: 'education', title: 'M.Sc. (Hons.) Mathematics & B.E. (Hons.) Civil Engineering', institution: 'BITS Pilani, India', period: '2015-2020' }], projects: [{ title: "Thermal Comfort Modeling", description: "Developed a 72% accurate, building-specific thermal comfort model using interpretable machine learning (SHAP, PDPs) on data from a custom-built app.", tags: ["Machine Learning", "App Development"], filterTag: "Thermal Comfort" }, { title: "Building Interfaces and Satisfaction", description: "Investigated occupant satisfaction in 11 Dutch offices, finding significant correlations between autonomy, competence, and satisfaction from 366 responses.", tags: ["Statistical Analysis", "User Satisfaction"], filterTag: "User Satisfaction" }, { title: "GPLAN - Floorplanning Tool", description: "Co-developed novel graph theory and optimization algorithms to instantly generate multiple floorplans from adjacency and dimensional constraints.", tags: ["Graph Theory", "Optimization"], filterTag: "Graph Theory" }, { title: "Rainwater Harvesting Network Optimisation", description: "Segmented satellite imagery using CNN and solved the Steiner Tree problem with a genetic algorithm to minimize pipeline costs.", tags: ["CNN", "Genetic Algorithms"], filterTag: "Optimization" }], apps: [{ icon: Smartphone, title: "Comfort App", description: "Developed for PhD research to collect real-time occupant comfort feedback via daily push notifications. Published on both major app stores.", tags: ["Mobile App"], link: "https://apps.apple.com/nl/app/b4b-building-28-delft/id6444261538?l=en-GB", color: "blue", image: "/comfort_app.png" }, { icon: Gamepad2, title: "GPLAN Game", description: "Co-developed an educational game where users generate valid floorplans by interpreting room adjacency graphs, guided by Prof. Shekhawat.", tags: ["Game Development"], link: "https://apps.apple.com/nl/app/gplan-game/id6727013926?l=en-GB", color: "green", image: "/gplan_game.png" }, { icon: LayoutGrid, title: "GPLAN.in", description: "The web tool behind the floorplanning research: give it room adjacencies and dimensional constraints and it generates every valid dimensioned floorplan instantly.", tags: ["Web App", "Graph Theory"], link: "https://gplan.in", color: "amber", image: "/gplan_web.jpg", imagePosition: "object-center" }, { icon: Building2, title: "ComfortOS", description: "An open-source, multi-tenant smart building platform enabling two-way occupant–building communication with server-driven UI, BMS integration, and real-time comfort analytics.", tags: ["React", "FastAPI", "Flutter"], link: "https://comfortos.netlify.app/", color: "purple", image: "/comfort-os.png" }, { icon: Shield, title: "P3Venti", description: "A decision-support tool for long-term care centers to manage pandemic risks like ventilation, balancing infection risk with resident health.", tags: ["Web App", "Decision Support"], link: "https://p3venti.netlify.app/", color: "red", status: "In Progress", image: "/paraat_app.png" }], teaching: { workshops: ["Graph-Theoretic algorithms for Building Architectural Floorplans (CAADRIA 2020).", "MATLAB for optimization, neural networks, and structural dynamics (BITS Pilani, 2019)."], supervision: ["Master Project: Interface design for all occupants of TU/e (2024).", "Tutor and Guest Lecturer: Smart Building Methodology and Technology, TU/e (2022-24)."] }, publications: [{ text: "Upasani, N., Guerra-Santin, O., & Mohammadi, M. (2024). Developing building-specific, occupant-centric thermal comfort models: A methodological approach.", journal: "Journal of Building Engineering, 95.", link: "https://www.sciencedirect.com/science/article/pii/S2352710224018497", tags: ['Thermal Comfort', 'Machine Learning'] }, { text: "Upasani, N., Shekhawat, K., & Sachdeva, G. (2020). Automated Generation of Dimensioned Rectangular Floorplans.", journal: "Automation in Construction, 113.", link: "https://doi.org/10.1016/j.autcon.2020.103149", tags: ['Automation', 'Graph Theory', 'Architecture'] }, { text: "Upasani, N., Guerra-Santin, M., Mohammadi, M., Seraj, M., & Joostens, F. (2025). Understanding thermal comfort using self-reporting and interpretable machine learning.", journal: "Energy Efficiency.", link: "https://link.springer.com/article/10.1007/s12053-025-10371-9", tags: ['Thermal Comfort', 'Machine Learning'] }, { text: "Upasani, N., Guerra-Santin, O., & Mohammadi, M. (2026). A self-determination theory approach to evaluating indoor environment satisfaction through building interfaces.", journal: "Building Research & Information.", link: "https://www.tandfonline.com/doi/full/10.1080/09613218.2026.2705259", tags: ['User Satisfaction'] }, { text: "Upasani, N., Guerra-Santin, O., & Mohammadi, M. (2025). Towards a standardized digital platform for smart buildings: Ensuring a two-way communication.", journal: "In preparation.", link: "#", tags: ['Smart Buildings'] }, { text: "Shekhawat, K., Upasani, N., Bisht, S., & Jain, R. (2021). A tool for computer-generated dimensioned floorplans based on given adjacencies.", journal: "Automation in Construction, 127.", link: "https://doi.org/10.1016/j.autcon.2021.103718", tags: ['Automation', 'Graph Theory', 'Architecture'] }, { text: "Bisht, S., Shekhawat, K., Upasani, N., Jain, R., Tiwaskar, R., & Hebbar, C. (2022). Transforming an Adjacency Graph into Dimensioned Floorplan Layouts.", journal: "Computer Graphics Forum, 41(6).", link: "https://doi.org/10.1111/cgf.14451", tags: ['Automation','Graph Theory', 'Architecture'] }, { text: "Nagpal, G., Chanda, U., & Upasani, N. (2022). Inventory replenishment policies for two successive generations price-sensitive technology products.", journal: "Journal of Industrial and Management Optimization, 18(3).", link: "https://doi.org/10.3934/jimo.2021036", tags: ['Optimization'] }, { text: "Rawat, S., Narula, R., Upasani, N., & Muthukumar, G. (2019). A relook on dosage of basalt chopped fibres and its influence on characteristics of concrete.", journal: "Advances in Structural Engineering and Rehabilitation.", link: "https://doi.org/10.1007/978-981-13-7615-3_22", tags: ['Civil Engineering'] }, { text: "Upasani, N., Bansal, M., Satapathy, A., Rawat, S., & Muthukumar, G. (2019). Design and Performance Criteria for Fire-Resistant Design of Structures--An Overview.", journal: "Advances in Structural Technologies.", link: "https://doi.org/10.1007/978-981-15-5235-9_21", tags: ['Civil Engineering', 'Structural Engineering'] }, { text: "Rawat, S., Narula, R., Kaushik, P., et al. (2024). Seismic and Fire Behaviour of FRP Strengthened Reinforced High Strength Concrete Structures-An Overview.", journal: "RC Structures Strengthened with FRP for Earthquake Resistance.", link: "https://doi.org/10.1007/978-981-97-0102-5_11", tags: ['Civil Engineering', 'Structural Engineering'] }, { text: "Rai, A., Upasani, N., Rawat, S., & Muthukumar, G. (2018). Methodology for numerical simulation of the behaviour of deep beams.", journal: "11th Structural Engineering Convention (SEC-2018).", link: "#", tags: ['Civil Engineering', 'Structural Engineering'] }, { text: "Upasani, N., & Gupta, R. (2019). Optimization of rainwater harvesting network in rural scenario using gis and ga.", journal: "5th International Conference on Soft Computing and Optimization.", link: "#", tags: ['Optimization', 'GIS', 'Genetic Algorithms','Civil Engineering'] }, { text: "Guerra-Santin, O., Lange, V., Upasani, N., Corsius, M., & Jeurens, J. (2025). User-centric interfaces for smart and healthy buildings: Exploring a design methodology.", journal: "Smart Healthy Environments (SHE) World Conference.", link: "#", tags: ['Smart Buildings', 'User Satisfaction'] }]};

// --- Animated Components ---
/* AnimatedBackground commented out
const AnimatedBackground = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    const nodes = Array.from({ length: Math.floor((canvas.width * canvas.height) / 20000) }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.5,
      radius: Math.random() * 1.5 + 1,
    }));
    const draw = () => {
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      nodes.forEach(node => {
        node.x += node.vx;
        node.y += node.vy;
        if (node.x < 0 || node.x > canvas.width) node.vx *= -1;
        if (node.y < 0 || node.y > canvas.height) node.vy *= -1;
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.35)';
        ctx.fill();
      });
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 120) {
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(255, 255, 255, ${0.08 * (1 - distance / 120)})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }
      animationFrameId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);
  return <canvas ref={canvasRef} className="absolute top-0 left-0 w-full h-full z-0" />;
};
*/

const TiltCard = ({ children, className }) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const xSpring = useSpring(x, { stiffness: 300, damping: 30 });
  const ySpring = useSpring(y, { stiffness: 300, damping: 30 });
  const rotateX = useTransform(ySpring, [-0.5, 0.5], ["7.5deg", "-7.5deg"]);
  const rotateY = useTransform(xSpring, [-0.5, 0.5], ["-7.5deg", "7.5deg"]);
  const handleMouseMove = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    x.set((e.clientX - rect.left) / rect.width - 0.5);
    y.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };
  return <motion.div ref={ref} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className={className}>{children}</motion.div>;
};

/* -------- FIXED CONSTANT-SPEED MARQUEE (true pause without reset) -------- */
const Marquee = ({ children, baseVelocity = 100 }) => {
  const marqueeRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  const pos = useMotionValue(0);
  const xStyle = useTransform(pos, v => -v);

  // keep a ref in sync so RAF can read pause state without re-creating the loop
  useEffect(() => { pausedRef.current = paused; }, [paused]);

  // measure width (content length of one copy)
  useEffect(() => {
    const calcWidth = () => {
      if (marqueeRef.current) setWidth(marqueeRef.current.scrollWidth);
    };
    calcWidth();
    const ro = new ResizeObserver(calcWidth);
    if (marqueeRef.current) ro.observe(marqueeRef.current);
    window.addEventListener('resize', calcWidth);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', calcWidth);
    };
  }, []);

  // RAF loop: depends only on width and velocity (NOT on paused)
  useEffect(() => {
    if (!width) return;
    let raf = 0;
    let last = performance.now();

    const tick = (now) => {
      const dt = (now - last) / 1000;
      last = now;

      if (!pausedRef.current) {
        // advance at constant speed and wrap inside [0, width)
        let next = pos.get() + baseVelocity * dt;
        if (next >= width) next = next % width;
        if (next < 0) next = (next % width + width) % width;
        pos.set(next);
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [width, baseVelocity, pos]);

  // if width changes (responsive), keep current position but wrap to new width
  useEffect(() => {
    if (!width) return;
    let raf = 0;
    let last = performance.now();

    const tick = (now) => {
      const dt = (now - last) / 1000;
      last = now;

      if (!pausedRef.current) {
        let next = pos.get() + baseVelocity * dt;
        if (next >= width) next = next % width;
        if (next < 0) next = (next % width + width) % width;
        pos.set(next);
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, baseVelocity]);
  return (
    <div
      className="overflow-hidden w-full"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <motion.div className="flex" style={{ x: xStyle }}>
        <div ref={marqueeRef} className="flex flex-shrink-0">{children}</div>
        <div className="flex flex-shrink-0" aria-hidden="true">{children}</div>
      </motion.div>
    </div>
  );
};
// ------------------------------------------------------------------------

// --- Reusable Components ---
const Section = ({ children, id, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const controls = useAnimationControls();
  useEffect(() => { if (isInView) controls.start("visible"); }, [isInView, controls]);
  return <motion.section id={id} ref={ref} className={`py-20 md:py-28 ${className}`} initial="hidden" animate={controls} variants={{ hidden: { opacity: 0, y: 50 }, visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } } }}>{children}</motion.section>;
};
const SectionTitle = ({ children }) => <h2 className="text-3xl md:text-4xl font-light text-center mb-12 md:mb-16 text-white">{children}</h2>;

// --- Page Section Components ---
const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  // Most links scroll to a section on this page; the dissertation link is a
  // route, so it gets the router's anchor props instead of a scroll handler.
  const navLinks = [
    { label: "About" },
    { label: "Apps" },
    { label: "Projects" },
    { label: "Teaching" },
    { label: "Publications" },
    { label: "Dissertation", to: "/dissertation" },
  ];
  const handleNavClick = (e, targetId) => {
    e.preventDefault();
    document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth' });
    setIsOpen(false);
  };
  const renderNavLink = ({ label, to }, className) => {
    if (to) {
      const props = linkProps(to);
      return (
        <a
          key={label}
          {...props}
          onClick={(e) => { props.onClick(e); setIsOpen(false); }}
          className={className}
        >
          {label}
        </a>
      );
    }
    const targetId = label.toLowerCase();
    return <a key={label} href={`#${targetId}`} onClick={(e) => handleNavClick(e, targetId)} className={className}>{label}</a>;
  };
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-white/10">
      <div className="container mx-auto px-6 py-4 flex justify-between items-center">
        <a href="/" className="text-sm font-medium text-white/50 hover:text-white transition-colors tracking-widest uppercase">{portfolioData.name}</a>
        <nav className="hidden md:flex items-center space-x-8">{navLinks.map(link => renderNavLink(link, "text-sm text-zinc-500 hover:text-white transition-colors tracking-wide"))}</nav>
        <div className="hidden md:flex items-center space-x-4">
          <a href={portfolioData.contact.github} target="_blank" rel="noopener noreferrer" className="text-zinc-600 hover:text-white transition-colors"><Github size={18} /></a>
          <a href={portfolioData.contact.linkedin} target="_blank" rel="noopener noreferrer" className="text-zinc-600 hover:text-white transition-colors"><Linkedin size={18} /></a>
          <a href={portfolioData.contact.googleScholar} target="_blank" rel="noopener noreferrer" className="text-zinc-600 hover:text-white transition-colors"><GoogleScholarIcon className="w-4 h-4" /></a>
          <a href={`mailto:${portfolioData.contact.email}`} className="text-zinc-600 hover:text-white transition-colors"><Mail size={18} /></a>
        </div>
        <button onClick={() => setIsOpen(!isOpen)} className="md:hidden text-white"><GitBranch size={22} /></button>
      </div>
      {isOpen && (
        <div className="md:hidden px-6 pb-4 flex flex-col space-y-4 bg-black/95">
          {navLinks.map(link => renderNavLink(link, "text-zinc-400 hover:text-white transition-colors block text-center text-sm tracking-wide"))}
        </div>
      )}
    </header>
  );
};
const Hero = () => {
  const name = "nitant upasani";
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { amount: 0.5 });
  const [displayedName, setDisplayedName] = useState("");
  const [showSubtitle, setShowSubtitle] = useState(false);
  const [key, setKey] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    setDisplayedName("");
    setShowSubtitle(false);
    setKey(k => k + 1);
  }, [isInView]);

  useEffect(() => {
    if (!isInView) return;
    let i = 0;
    const timer = setInterval(() => {
      if (i < name.length) {
        setDisplayedName(name.slice(0, i + 1));
        i++;
      } else {
        clearInterval(timer);
        setTimeout(() => setShowSubtitle(true), 300);
      }
    }, 80);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return (
    <section ref={sectionRef} id="home" className="min-h-screen flex flex-col items-center justify-center bg-black">
      {/* <AnimatedBackground /> */}
      <div className="text-center px-6 z-10">
        <h1 className="text-5xl md:text-7xl font-light text-white tracking-[0.15em]">
          {displayedName}
        </h1>
        <motion.p
          className="mt-5 text-sm md:text-base text-zinc-500 tracking-[0.3em] uppercase"
          initial={{ opacity: 0 }}
          animate={{ opacity: showSubtitle ? 1 : 0 }}
          transition={{ duration: 1.5 }}
        >
          Researcher · Designer · Developer
        </motion.p>
      </div>
    </section>
  );
};
const About = () => {
  const [activeTab, setActiveTab] = useState('philosophy');
  const tabs = ['philosophy', 'strengths', 'values'];
  return <Section id="about" className="bg-zinc-950"><div className="container mx-auto px-6"><SectionTitle>About Me</SectionTitle><div className="grid md:grid-cols-5 gap-12 items-start"><div className="md:col-span-2"><div className="mb-4 border-b border-white/10"><ul className="flex flex-wrap -mb-px text-sm font-medium text-center">{tabs.map(tab => <li key={tab} className="mr-2"><button onClick={() => setActiveTab(tab)} className={`inline-block p-4 border-b-2 rounded-t-lg capitalize transition-colors ${activeTab === tab ? 'text-white border-white' : 'text-zinc-500 border-transparent hover:text-zinc-300 hover:border-zinc-600'}`}>{tab}</button></li>)}</ul></div><div className="p-4 rounded-lg bg-zinc-900 min-h-[120px]"><p className="text-zinc-400">{portfolioData.about[activeTab]}</p></div><div className="mt-8"><h3 className="text-xl font-semibold text-white mb-4">Technical Proficiency</h3><div className="flex flex-wrap gap-2">{portfolioData.skills.map(skill => <span key={skill} className="bg-white/10 text-zinc-400 text-xs font-mono px-3 py-1 rounded-full">{skill}</span>)}</div></div></div><div className="md:col-span-3"><h3 className="text-xl font-semibold text-white mb-6">Education & Experience</h3><motion.ol className="relative border-l border-white/10" variants={{ visible: { transition: { staggerChildren: 0.1 } } }}>{portfolioData.timeline.map((item, index) => <motion.li key={index} className="mb-10 ml-8" variants={{ hidden: { opacity: 0, x: -20 }, visible: { opacity: 1, x: 0 } }}><span className="absolute flex items-center justify-center w-8 h-8 bg-zinc-800 rounded-full -left-4 ring-4 ring-zinc-950 text-white">{item.type === 'work' ? <Briefcase size={16} /> : <GraduationCap size={16} />}</span><h3 className="flex items-center mb-1 text-lg font-semibold text-white">{item.title}</h3><time className="block mb-2 text-sm font-normal leading-none text-zinc-600">{item.period} | {item.institution}</time>{item.description && <p className="mb-4 text-base font-normal text-zinc-400">{item.description}</p>}</motion.li>)}</motion.ol></div></div></div></Section>;
};
const Projects = ({ setActiveFilter }) => {
  const handleProjectClick = (tag) => {
    setActiveFilter(tag);
    document.getElementById('publications')?.scrollIntoView({ behavior: 'smooth' });
  };
  return (
    <Section id="projects" className="bg-black">
      <div className="container mx-auto">
        <SectionTitle>Research Projects</SectionTitle>
        <Marquee baseVelocity={100}>
          {portfolioData.projects.map((project, index) => (
            <div key={index} className="w-[90vw] max-w-[400px] md:w-[350px] mx-4 flex-shrink-0 cursor-pointer" onClick={() => handleProjectClick(project.filterTag)}>
              <TiltCard className="h-full">
                <div className="h-full flex flex-col bg-zinc-900 rounded-lg p-6 border border-white/10">
                  <h3 className="text-xl font-bold text-white mb-2">{project.title}</h3>
                  <p className="text-zinc-400 mb-4 flex-grow whitespace-normal">{project.description}</p>
                  <div className="flex flex-wrap gap-2 mt-auto">
                    {project.tags.map(tag => <span key={tag} className="bg-white/10 text-zinc-400 text-xs font-mono px-3 py-1 rounded-full">{tag}</span>)}
                  </div>
                </div>
              </TiltCard>
            </div>
          ))}
        </Marquee>
      </div>
    </Section>
  );
};
const Apps = () => {
  return (
    <Section id="apps" className="bg-zinc-950">
      <div className="container mx-auto">
        <SectionTitle>Applications Developed</SectionTitle>
        <Marquee baseVelocity={100}>
          {portfolioData.apps.map((app, index) => {
            const Icon = app.icon;
            const isClickable = app.link && app.link !== "#";
            const CardContent = (
              <div className="relative flex flex-col h-full bg-zinc-900 rounded-lg border border-white/10 overflow-hidden">
                {app.image && <div className="w-full h-44 overflow-hidden"><img src={app.image} alt={`${app.title} screenshot`} className={`w-full h-full object-cover ${app.imagePosition || 'object-top'}`} /></div>}
                <div className={`flex flex-col flex-grow ${app.image ? 'p-4' : 'p-6'}`}>
                  {app.status === "In Progress" && <div className="absolute top-2 right-2 z-10 flex items-center bg-zinc-700 text-zinc-300 text-xs font-bold px-3 py-1 rounded-full shadow-md"><Wrench size={12} className="mr-1.5" />{app.status}</div>}
                  <div className="flex items-center mb-4"><div className="p-3 rounded-full bg-white/10 mr-4 text-white"><Icon size={24} /></div><h3 className="text-xl font-bold text-white">{app.title}</h3></div>
                  <p className="text-zinc-400 mb-4 flex-grow whitespace-normal">{app.description}</p>
                  <div className="flex justify-between items-center mt-auto"><div className="flex flex-wrap gap-2">{app.tags.map(tag => <span key={tag} className="bg-white/10 text-zinc-400 text-xs font-mono px-3 py-1 rounded-full">{tag}</span>)}</div>{isClickable && <ArrowRight className="text-zinc-600" size={20} />}</div>
                </div>
              </div>
            );
            const Wrapper = ({ children }) => isClickable ? <a href={app.link} target="_blank" rel="noopener noreferrer" className="block h-full">{children}</a> : <>{children}</>;
            return (
              <div key={index} className="w-[90vw] max-w-[400px] md:w-[350px] mx-4 flex-shrink-0">
                <TiltCard className="h-full">
                  <Wrapper>{CardContent}</Wrapper>
                </TiltCard>
              </div>
            );
          })}
        </Marquee>
      </div>
    </Section>
  );
};
const Teaching = () => (
  <Section id="teaching" className="bg-black">
    <div className="container mx-auto px-6">
      <SectionTitle>Teaching & Mentorship</SectionTitle>
      <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
        <div className="bg-zinc-900 p-6 rounded-lg border border-white/10">
          <h3 className="font-semibold text-lg text-white mb-3">Workshops Conducted</h3>
          <ul className="space-y-2 text-zinc-400 list-disc list-inside">
            {portfolioData.teaching.workshops.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </div>
        <div className="bg-zinc-900 p-6 rounded-lg border border-white/10">
          <h3 className="font-semibold text-lg text-white mb-3">Student Supervision</h3>
          <ul className="space-y-2 text-zinc-400 list-disc list-inside">
            {portfolioData.teaching.supervision.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </div>
      </div>
    </div>
  </Section>
);
const Publications = ({ activeFilter, setActiveFilter }) => {
    const curatedTags = ['All', 'Machine Learning', 'Graph Theory', 'Automation', 'Civil Engineering', 'Optimization', 'Thermal Comfort', 'User Satisfaction', 'Smart Buildings'];
    const filteredPublications = activeFilter === 'All' ? portfolioData.publications : portfolioData.publications.filter(p => p.tags && p.tags.includes(activeFilter));
    return (
      <Section id="publications" className="bg-zinc-950"><div className="container mx-auto px-6"><SectionTitle>Publications</SectionTitle><div className="flex flex-wrap justify-center gap-2 mb-12">{curatedTags.map(tag => <button key={tag} onClick={() => setActiveFilter(tag)} className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${activeFilter === tag ? 'bg-white text-black' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white'}`}>{tag}</button>)}</div><motion.div layout className="max-w-4xl mx-auto space-y-4"><AnimatePresence>{filteredPublications.map((pub) => <motion.a key={pub.text} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} href={pub.link} target="_blank" rel="noopener noreferrer" className="block p-4 border border-white/10 rounded-lg bg-zinc-900 hover:border-white/30 transition-colors"><p className="font-medium text-zinc-200">{pub.text} <em className="text-zinc-500 not-italic">{pub.journal}</em></p></motion.a>)}</AnimatePresence></motion.div></div></Section>
    );
};
const Footer = () => (
  <footer className="bg-zinc-950 border-t border-white/10"><div className="container mx-auto px-6 py-8 text-center text-zinc-500"><p className="text-lg font-semibold text-white">{portfolioData.name}</p><p className="mt-2">Let's connect and build something great together.</p><div className="mt-6 flex justify-center space-x-6"><a href={portfolioData.contact.github} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors"><Github size={22} /></a><a href={`mailto:${portfolioData.contact.email}`} className="hover:text-white transition-colors"><Mail size={22} /></a><a href={portfolioData.contact.linkedin} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors"><Linkedin size={22} /></a><a href={portfolioData.contact.googleScholar} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors"><GoogleScholarIcon className="w-5 h-5" /></a></div><p className="mt-8 text-sm text-zinc-600">&copy; {new Date().getFullYear()} {portfolioData.name}. All rights reserved.</p></div></footer>
);

const Home = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  return (
    <div className="bg-black text-white font-sans">
      <Header />
      <main>
        <Hero />
        <About />
        <Apps />
        <Projects setActiveFilter={setActiveFilter} />
        <Teaching />
        <Publications activeFilter={activeFilter} setActiveFilter={setActiveFilter} />
      </main>
      <Footer />
    </div>
  );
};

export default function App() {
  const route = useRoute();
  return (
    <ThemeProvider>
      {route === '/dissertation' ? <Dissertation /> : <Home />}
    </ThemeProvider>
  );
}
